/**
 * D'où vient un objet ? Module pur.
 *
 * Sans cette réponse, une recette est une impasse : on lit « il manque 2
 * Ébonite » sans savoir s'il faut la farmer, la récolter, la crafter, ou si
 * elle est hors d'atteinte dans la tranche de contenu chargée.
 */
import type { Contenu } from "./contenu.ts"
import { PEPITE, TOURMALINE } from "./objets.ts"

export type Origine =
  | { type: "drop"; familles: { id: number; nom: string; taux: number; zone?: number | null }[] }
  | { type: "recolte"; metier: number; nomMetier: string; niveau: number }
  | {
      type: "donjon"
      /** les plus accessibles, du plus bas niveau au plus haut */
      donjons: { id: number; nom: string; boss: string; niveau: number; taux: number }[]
      /** combien en lâchent au total : certains butins viennent de 125 boss */
      total: number
    }
  | { type: "archimonstre"; nom: string; niveau: number }
  | { type: "recherche"; nom: string; niveau: number }
  | { type: "craft" }
  /**
   * Ce que rend le broyage d'un équipement (GDD §8).
   *
   * La Pépite et la Tourmaline ne tombent d'aucun monstre et n'ont pas de
   * recette : le broyeur les produit. `provenance.ts` l'ignorait, si bien que
   * **396 recettes sur 3 531** — dont toute la panoplie Terrdala — s'affichaient
   * « hors d'atteinte », alors qu'il suffit de broyer une pièce du coffre.
   * Signalé par Yuter le 27/08/2026 : « je peux plus craft la hache, ça me dit
   * qu'un objet n'est plus dans le jeu ».
   */
  | { type: "broyage" }
  | { type: "inconnue" }

/** Index calculé une fois pour tout le contenu. */
export interface Index {
  par: Map<number, Origine>
}

export function indexer(c: Contenu): Index {
  const par = new Map<number, Origine>()

  // récolte : la source la plus directe, elle prime
  for (const r of Object.values(c.recoltes)) {
    par.set(r.item, {
      type: "recolte", metier: r.metier,
      nomMetier: c.metiers[String(r.metier)]?.nom ?? "",
      niveau: r.niveauRequis,
    })
  }

  // butin : on retient les familles qui le lâchent, du meilleur taux au pire
  const drops = new Map<number, { id: number; nom: string; taux: number; zone?: number | null }[]>()
  for (const f of Object.values(c.familles)) {
    for (const mid of f.monstres) {
      const m = c.monstres[String(mid)]
      if (!m) continue
      for (const b of m.butin) {
        const taux = Math.max(...b.taux)
        const liste = drops.get(b.item) ?? []
        const deja = liste.find((x) => x.id === f.id)
        if (deja) deja.taux = Math.max(deja.taux, taux)
        else liste.push({ id: f.id, nom: f.nom, taux, zone: f.zone })
        drops.set(b.item, liste)
      }
    }
  }
  for (const [item, liste] of drops) {
    if (par.has(item)) continue
    par.set(item, { type: "drop", familles: liste.sort((a, b) => b.taux - a.taux).slice(0, 3) })
  }

  // Boss de donjon, archimonstres, recherchés : **ils ne sont dans aucune
  // famille**, et l'index les ignorait.
  //
  // Ce sont pourtant 524 monstres sur 1416, et 1065 objets n'avaient qu'eux
  // pour source : autant de recettes marquées « aucune source connue » alors
  // qu'un donjon ou une capture y menait. « Plume du Kwakwa » tombe du boss du
  // Nid du Kwakwa ; il n'y avait rien d'introuvable là-dedans.
  // On retient **les donjons les plus accessibles**, pas le premier venu.
  //
  // Certains butins sont communs à des dizaines de boss : le Galet cramoisi
  // tombe de cent vingt-cinq d'entre eux. N'en montrer qu'un, au hasard de
  // l'ordre des données, désignait « Maître Corbac » — niveau 100, clé exigeant
  // trois métiers au niveau 100 — pour un ingrédient de recette niveau 50, alors
  // qu'un donjon de niveau 20 le lâche aussi.
  const parDonjon = new Map<number, { id: number; nom: string; boss: string; niveau: number; taux: number }[]>()
  for (const d of Object.values(c.donjons)) {
    for (const bid of d.boss) {
      const m = c.monstres[String(bid)]
      if (!m) continue
      const niveau = d.niveauMin || d.niveauOptimal || 200
      for (const b of m.butin) {
        const liste = parDonjon.get(b.item) ?? []
        // Le taux de chute compte autant que le lieu : « quatre donjons sans
        // rien lâcher » ne se comprend qu'en sachant qu'il tombe à 2 %.
        if (!liste.some((x) => x.id === d.id)) {
          liste.push({ id: d.id, nom: d.nom, boss: m.nom, niveau, taux: Math.max(...b.taux) })
        }
        parDonjon.set(b.item, liste)
      }
    }
  }
  for (const [item, liste] of parDonjon) {
    if (par.has(item)) continue
    const tries = liste.sort((a, b) => a.niveau - b.niveau)
    par.set(item, { type: "donjon", donjons: tries.slice(0, 3), total: tries.length })
  }

  // craft avant les collections : fabriquer soi-même est plus actionnable que
  // guetter l'apparition d'un archimonstre.
  for (const r of c.recettes) {
    if (!par.has(r.resultat)) par.set(r.resultat, { type: "craft" })
  }

  // Même principe : le plus bas niveau d'abord, c'est le plus atteignable.
  for (const [table, type] of [[c.archimonstres, "archimonstre"], [c.recherches, "recherche"]] as const) {
    const tries = Object.values(table ?? {})
      .map((x) => x as { id: number; niveau?: number })
      .sort((a, b) => (a.niveau ?? 200) - (b.niveau ?? 200))
    for (const x of tries) {
      const m = c.monstres[String(x.id)]
      if (!m) continue
      for (const b of m.butin) {
        if (par.has(b.item)) continue
        par.set(b.item, { type, nom: m.nom, niveau: x.niveau ?? 0 })
      }
    }
  }

  // Le broyeur, en dernier : si jamais un contenu futur donnait une autre source
  // à ces deux pierres, elle passerait devant, et c'est très bien.
  for (const id of [PEPITE, TOURMALINE]) {
    if (!par.has(id) && c.items[String(id)]) par.set(id, { type: "broyage" })
  }

  return { par }
}

export const origine = (idx: Index, item: number): Origine =>
  idx.par.get(item) ?? { type: "inconnue" }

/** Résumé court, pour tenir sur une ligne d'ingrédient. */
export function libelleOrigine(o: Origine): string {
  switch (o.type) {
    case "recolte": return `${o.nomMetier} niv ${o.niveau}`
    case "drop": return o.familles.map((f) => f.nom).join(", ")
    // Le plus accessible en tête, et l'on dit qu'il y en a d'autres : « tombe
    // de Maître Corbac » laissait croire à une source unique de niveau 100.
    case "donjon": {
      const [d] = o.donjons
      const reste = o.total - 1
      // Beaucoup de donjons s'appellent déjà « Donjon des… » : préfixer donnait
      // « donjon Donjon des Bworks ».
      const nom = d ? (/^donjon/i.test(d.nom) ? d.nom : `donjon ${d.nom}`) : "donjon"
      return `${nom} (niv ${d?.niveau ?? "?"})${reste > 0 ? ` et ${reste} autre${reste > 1 ? "s" : ""}` : ""}`
    }
    case "archimonstre": return `${o.nom}, archimonstre`
    case "recherche": return `${o.nom}, recherché`
    case "craft": return "à fabriquer"
    case "broyage": return "en broyant un équipement"
    // Ni butin, ni récolte, ni recette : l'objet existe dans les données du jeu
    // mais aucun chemin n'y mène ici. C'était écrit « hors de la tranche
    // chargée », vrai du temps où le jeu n'embarquait que les niveaux 1 à 60,
    // incompréhensible depuis qu'il les embarque tous.
    default: return "aucune source connue"
  }
}

/**
 * Où aller pour s'en procurer, encodé pour l'attribut `data-aller`.
 *
 * Le libellé d'origine ne servait qu'à lire ; il fallait ensuite retrouver la
 * zone à la main. `null` quand il n'y a nulle part où envoyer le joueur.
 */
export function destination(o: Origine, nom: string): string | null {
  switch (o.type) {
    // la famille qui le lâche le plus souvent, dans sa zone
    case "drop": {
      const f = o.familles.find((x) => x.zone != null) ?? o.familles[0]
      return f?.zone != null ? `zone:${f.zone}:mob` : null
    }
    // les ressources ne sont pas rattachées à une zone : la recherche du monde
    // les trouve et propose la récolte directement
    case "recolte": return `monde:${nom}`
    // **La recherche du monde**, pas la liste des cent vingt-six donjons.
    //
    // Y renvoyer laissait le joueur devant un mur sans savoir lequel ouvrir.
    // La recherche, elle, nomme l'objet et aligne ses sources — les trois
    // donjons les plus accessibles, chacun menant à sa zone.
    case "donjon": return `monde:${nom}`
    // Même raison : on ne « va » pas chercher un archimonstre, il apparaît. La
    // recherche dit au moins lequel guetter.
    case "archimonstre": return `monde:${nom}`
    case "recherche": return `monde:${nom}`
    case "craft": return `atelier:${nom}`
    // Le broyeur vit dans le coffre : c'est là qu'on choisit la pièce à briser.
    case "broyage": return "coffre"
    default: return null
  }
}


/**
 * **Toutes** les sources d'un objet, calculées à la demande.
 *
 * L'index n'en retient que trois — les plus accessibles — parce que garder les
 * cent seize donjons qui lâchent un Galet cramoisi, pour chacun des cinq mille
 * objets, alourdirait la mémoire sans servir la lecture courante. Mais « et 115
 * autres » sans moyen de les voir n'aide personne : cette fonction déplie la
 * liste entière pour un objet, quand on la demande.
 */
export function sourcesCompletes(c: Contenu, item: number): {
  familles: { id: number; nom: string; taux: number; zone?: number | null }[]
  donjons: { id: number; nom: string; boss: string; niveau: number; taux: number }[]
} {
  const familles: { id: number; nom: string; taux: number; zone?: number | null }[] = []
  for (const f of Object.values(c.familles)) {
    let taux = 0
    for (const mid of f.monstres) {
      for (const b of c.monstres[String(mid)]?.butin ?? []) {
        if (b.item === item) taux = Math.max(taux, Math.max(...b.taux))
      }
    }
    if (taux > 0) familles.push({ id: f.id, nom: f.nom, taux, zone: f.zone })
  }

  const donjons: { id: number; nom: string; boss: string; niveau: number; taux: number }[] = []
  for (const d of Object.values(c.donjons)) {
    for (const bid of d.boss) {
      const m = c.monstres[String(bid)]
      const b = m?.butin?.find((x) => x.item === item)
      if (!m || !b) continue
      donjons.push({
        id: d.id, nom: d.nom, boss: m.nom,
        niveau: d.niveauMin || d.niveauOptimal || 200,
        taux: Math.max(...b.taux),
      })
      break
    }
  }

  return {
    familles: familles.sort((a, b) => b.taux - a.taux),
    donjons: donjons.sort((a, b) => a.niveau - b.niveau),
  }
}

/**
 * **Ce qu'une ressource permet de fabriquer.**
 *
 * L'inverse de `sourcesCompletes` : celle-ci dit d'où vient un objet, celle-là
 * où il va. Deux joueurs ont buté sur le même mur à un jour d'écart — Balbouz
 * « voir à partir d'une ressource ce qu'on peut craft avec », Vyshu « je peux
 * faire quoi de l'avoine ? il manque quoi comme ingrédients ? ». L'Avoine entre
 * dans quatorze recettes, dont douze clés de donjon : l'information était là,
 * simplement inatteignable depuis la ressource qu'on tient en main.
 *
 * On rend aussi **ce qui manque** pour chaque recette, parce que la vraie
 * question n'est pas « que puis-je fabriquer un jour » mais « qu'est-ce qui me
 * manque maintenant ».
 */
export function utilisations(
  c: Contenu, item: number, inventaire: Readonly<Record<string, number>>,
): {
  resultat: number
  nom: string
  niveau: number
  /** combien il en faut pour une fabrication */
  qte: number
  /** réalisable avec ce qu'on a en réserve */
  ok: boolean
  /** les ingrédients qui manquent, du plus proche au plus lointain */
  manque: { item: number; nom: string; reste: number }[]
}[] {
  const sortie = []
  for (const r of c.recettes) {
    const g = r.ingredients.find((x) => x.item === item)
    if (!g) continue
    const it = c.items[String(r.resultat)]
    if (!it) continue
    const manque = []
    for (const x of r.ingredients) {
      const a = inventaire[String(x.item)] ?? 0
      if (a >= x.qte) continue
      const nom = c.items[String(x.item)]?.nom
      if (!nom) continue
      manque.push({ item: x.item, nom, reste: x.qte - a })
    }
    manque.sort((a, b) => a.reste - b.reste)
    sortie.push({
      resultat: r.resultat, nom: it.nom, niveau: it.niveau ?? 0,
      qte: g.qte, ok: manque.length === 0, manque,
    })
  }
  // Ce qu'on peut faire tout de suite d'abord, puis du plus proche au plus
  // lointain : c'est l'ordre dans lequel on se pose la question.
  sortie.sort((a, b) =>
    Number(b.ok) - Number(a.ok) || a.manque.length - b.manque.length || a.niveau - b.niveau)
  return sortie
}
